import { useEffect, useState } from "react";
import {
  ReceiptVoucherUpdate,
  ReceiptVoucherCreate,
  GetReceiptVoucherById,
  FilteredSalesVoucherList,
  GenerateReceiptVoucherNo,
  BankDetailList,
  DivisionList,
  ApprovePartyList,
  SalesOrderListApi,
  FilteredProformaInvoiceList,
} from "../../../Services/Services";
import {
  Box,
  TextField,
  Button,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Typography,
  Paper,
  Card,
  CardContent,
  Divider,
  Autocomplete,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";

const ReceiptVoucherForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // 🔹 Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();

  const isEditMode = location.state?.editMode || false;
  const voucherId = location.state?.voucherId || null;

  function blankRow() {
    return {
      receipt_voucher_no: "",
      receipt_number: "",
      // project: "",
      project_name: "",
      party: "",
      party_name: "",
      invoice_number: "",
      amount: "",
      attachment: null,
      payment_mode: "",
      bank: "",
      invoice_date: "",
      narration: "",
      proforma_invoice: "",
      description: "",
      cheque_no: "",
      sales_voucher: "",
      voucher_type: "",
      division: storedDivision?.division_id || "",
      rec_amt: 0.0,
      receipt_date: "",
      cheque_date: "",
      created_by: localStorage.getItem("user_id") || "",
      receipt_date: "", // ✅ Empty string se start karo
      cheque_date: "",
    };
  }

  const [rows, setRows] = useState([blankRow()]);
  const [formErrors, setFormErrors] = useState({});
  const [purchaseLists, setPurchaseLists] = useState([]); // PI/Advance data
  const [saleList, setSaleList] = useState([]); // Sales voucher data
  const [bankDetails, setBankDetails] = useState([]);
  const [editMode, setEditMode] = useState(isEditMode);
  const [currentEditId, setCurrentEditId] = useState(voucherId);
  const [partyList, setPartyList] = useState([]); // Party data
  const [projectList, setProjectList] = useState([]);
  const [selectedPartyId, setSelectedPartyId] = useState(null);
  const [existingFileName, setExistingFileName] = useState("");

  const fetchPartyList = async () => {
    const party_response = await ApprovePartyList();

    setPartyList(party_response || []); // <-- sirf results
  };

  const fetchProjectList = async (query = "") => {
    try {
      const res = await SalesOrderListApi(1, { project_name: query });
      setProjectList(res?.results || []);
    } catch (err) {
      setProjectList([]);
      toast.error("Error fetching projects");
    }
  };

  const handleProjectChange = (index, selectedProject) => {
    setRows((prev) => {
      const updated = [...prev];
      const currentRow = updated[index];

      if (selectedProject) {
        const projId = selectedProject.id ?? selectedProject.id ?? "";
        const projName =
          selectedProject.project_name ?? selectedProject.name ?? "";

        updated[index] = {
          ...currentRow,
          // ✅ Set project
          project: projId,
          project_name: projName,

          // ✅ Auto-fill debtor & division from project
          party: selectedProject.party ?? "",
          party_name:
            selectedProject.company_name ?? selectedProject.party_name ?? "",
          division: storedDivision?.division_id || currentRow.division || "",
          division_name:
            storedDivision?.division || currentRow.division_name || "",

          // ✅ Clear voucher type section
          voucher_type: "",
          proforma_invoice: "",
          sales_voucher: "",
          amount: "",
          rec_amt: 0.0,
        };

        // ✅ Update selected party ID
        if (selectedProject.party) {
          setSelectedPartyId(selectedProject.party);
        }
      } else {
        // ✅ Clear everything when project is cleared
        updated[index] = {
          ...currentRow,
          project: "",
          project_name: "",
          party: "",
          party_name: "",
          division: storedDivision?.division_id || "",
          division_name: storedDivision?.division || "",
          voucher_type: "",
          proforma_invoice: "",
          sales_voucher: "",
          amount: "",
          rec_amt: 0.0,
        };

        setSelectedPartyId(null);
      }

      return updated;
    });

    // ✅ Clear PI/Sale lists when project changes
    setPurchaseLists([]);
    setSaleList([]);
  };
  // ✅ Fix 2: handlePartyChange - project ko preserve karo
  const handlePartyChange = (index, selectedParty) => {
    setRows((prev) => {
      const updated = [...prev];
      const currentRow = updated[index];

      if (selectedParty) {
        const partyId = selectedParty.party_id || selectedParty.id || "";

        updated[index] = {
          ...currentRow,
          party: partyId,
          party_name:
            selectedParty.company_name ||
            selectedParty.name ||
            selectedParty.party_name ||
            "",

          // ✅ Clear PI/Sale when party changes
          proforma_invoice: "",
          sales_voucher: "",
          amount: "",
        };

        // ✅ Store party ID
        setSelectedPartyId(partyId);

        // ✅ Reload PI list if advance selected
        if (currentRow.voucher_type === "advance") {
          fetchAdvanceData(partyId);
        }

        // ✅ Reload Sales list if sale selected (NEW)
        if (currentRow.voucher_type === "sale") {
          fetchSaleData(partyId);
        }
      } else {
        updated[index] = {
          ...currentRow,
          party: "",
          party_name: "",
          proforma_invoice: "",
          sales_voucher: "",
          amount: "",
        };

        setSelectedPartyId(null);
        setPurchaseLists([]);
        setSaleList([]); // ✅ Clear sales list
      }

      return updated;
    });
  };

  // ✅ Fix 3: handleDivisionChange - project ko preserve karo
  const handleDivisionChange = (index, selectedDivision) => {
    setRows((prev) => {
      const updated = [...prev];
      const currentRow = updated[index]; // ✅ Current row preserve

      if (selectedDivision) {
        updated[index] = {
          ...currentRow, // ✅ Preserve project, party, etc.
          division: selectedDivision.division_id,
          division_name: selectedDivision.division || "",
        };
      } else {
        updated[index] = {
          ...currentRow, // ✅ Preserve other fields
          division: "",
          division_name: "",
        };
      }

      return updated;
    });
  };

  // ✅ Fix 4: handleAdvanceChange - manually selected values preserve
  const handleAdvanceChange = (index, selectedAdvance) => {
    if (selectedAdvance) {
      setRows((prev) => {
        const updated = [...prev];
        const currentRow = updated[index];

        updated[index] = {
          ...currentRow,
          proforma_invoice: selectedAdvance.invoice_number,
          amount: selectedAdvance.advance_received?.toFixed(2) || "",

          // Auto-fill only if empty
          project:
            currentRow.project ||
            selectedAdvance.project_id ||
            selectedAdvance.project?.id ||
            "",
          project_name:
            currentRow.project_name ||
            selectedAdvance.project_name ||
            selectedAdvance.project?.project_name ||
            "",

          // ✅ Party should match already selected party (don't override)
          party:
            currentRow.party ||
            selectedAdvance.party_id ||
            selectedAdvance.party?.id ||
            "",
          party_name:
            currentRow.party_name ||
            selectedAdvance.company_name ||
            selectedAdvance.party_name ||
            "",

          division:
            currentRow.division ||
            selectedAdvance.division ||
            selectedAdvance.project?.division ||
            "",
          division_name:
            currentRow.division_name ||
            selectedAdvance.division_name ||
            selectedAdvance.project?.division_name ||
            "",
        };

        return updated;
      });
    }
  };

  // ✅ Fix 5: handleSaleChange - manually selected values preserve
  const handleSaleChange = (index, selectedSale) => {
    if (selectedSale) {
      setRows((prev) => {
        const updated = [...prev];
        const currentRow = updated[index];

        updated[index] = {
          ...currentRow,
          sales_voucher: selectedSale.sales_voucher_no,
          amount: selectedSale.total_amount?.toFixed(2) || "",

          // Auto-fill only if empty
          project:
            currentRow.project ||
            selectedSale.project_id ||
            selectedSale.project ||
            "",
          project_name:
            currentRow.project_name || selectedSale.project_name || "",

          // ✅ Party should match already selected party
          party:
            currentRow.party ||
            selectedSale.party_id ||
            selectedSale.party ||
            "",
          party_name:
            currentRow.party_name ||
            selectedSale.company_name ||
            selectedSale.party_name ||
            "",

          division: currentRow.division || selectedSale.division || "",
          division_name:
            currentRow.division_name || selectedSale.division_name || "",
        };

        return updated;
      });
    }
  };

  useEffect(() => {
    fetchPartyList();
    fetchProjectList();
  }, []);

  useEffect(() => {
    if (editMode && currentEditId) {
      fetchVoucherData(currentEditId);
    } else {
      generateVoucherNumber();
    }
  }, [editMode, currentEditId]);

  const fetchVoucherData = async (id) => {
    try {
      const response = await GetReceiptVoucherById(id);

      if (response) {
        
        if (response.attachment) {
          const fullPath = response.attachment.split("/").pop();
          const fileName = fullPath.split("?")[0];

          if (fileName.length > 20) {
            const ext = fileName.split(".").pop();
            const name = fileName.substring(0, 15);
            setExistingFileName(`${name}...${ext}`);
          } else {
            setExistingFileName(fileName);
          }
        }

        // ✅ Set party ID first
        if (response.party) {
          setSelectedPartyId(response.party);
        }

        // ✅ CRITICAL: Load lists BEFORE setting form data
        if (response.voucher_type === "advance" && response.party) {
          await fetchAdvanceData(response.party);
        } else if (response.voucher_type === "sale" && response.party) {
          await fetchSaleData(response.party); // ✅ This must complete
        }
        if (response.payment_mode === "Bank") {
          await fetchBankData();
        }

        // Then populate the form with fetched data
        const voucherData = {
          receipt_voucher_no: response.receipt_voucher_no || "",
          receipt_number: response.receipt_number || "",
          project: response.project || "",
          project_name: response.project_name || "",
          party: response.party || "",
          party_name: response.party_name || "",
          invoice_number: response.invoice_number || "",
          amount: response.amount || "",
          division: storedDivision?.division_id || response.division || "", 
        division_name: storedDivision?.division || response.division_name || "",
          rec_amt: response.rec_amt || 0,
          payment_mode: response.payment_mode || "",
          bank: response.bank || "",
          invoice_date: response.invoice_date || "",
          narration: response.narration || "",
          proforma_invoice: response.proforma_invoice || "",
          description: response.description || "",
          cheque_no: response.cheque_no || "",
          sales_voucher: response.sales_voucher || "",
          voucher_type: response.voucher_type || "",
          receipt_date: response.receipt_date || "",
          cheque_date: response.cheque_date || "",
          created_by:
            response.created_by || localStorage.getItem("user_id") || "",
          attachment: response.attachment || null, // Keep existing attachment reference
        };

        setRows([voucherData]);
      }
    } catch (error) {
      toast.error("Failed to fetch voucher data");
      navigate("/dashboard/receiptvoucher");
    }
  };

  const generateVoucherNumber = async () => {
    try {
      const data = await GenerateReceiptVoucherNo();
      if (data && data.data?.receipt_voucher_no) {
        setRows((prev) => {
          const updated = [...prev];
          if (updated.length > 0) {
            updated[0] = {
              ...updated[0],
              receipt_number: data.data.receipt_voucher_no,
            };
          }
          return updated;
        });
      }
    } catch (error) {
      toast.error("Failed to generate voucher number");
    }
  };
  const fetchAdvanceData = async (partyId = null, projectId = null) => {
    try {
      const advanceData = await FilteredProformaInvoiceList(partyId, projectId); // ✅ CHANGED

      setPurchaseLists(
        Array.isArray(advanceData) ? advanceData : advanceData?.results || [],
      );
    } catch (err) {
      toast.error("Error fetching advance/PI data");
      setPurchaseLists([]); // ✅ ADDED - clear list on error
    }
  };

  const fetchSaleData = async (partyId = null) => {
    try {
      const saleData = await FilteredSalesVoucherList(partyId); // ✅ CHANGED

      setSaleList(Array.isArray(saleData) ? saleData : saleData?.results || []);
    } catch (err) {
      toast.error("Error fetching sale voucher data");
      setSaleList([]); // ✅ Clear list on error
    }
  };
  const fetchBankData = async () => {
    try {
      const bank = await BankDetailList();
      setBankDetails(Array.isArray(bank) ? bank : bank?.results || []);
    } catch (err) {
      toast.error("Error fetching bank details");
    }
  };
  const handleVoucherTypeChange = (index, type) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        voucher_type: type,
        proforma_invoice: "",
        sales_voucher: "",
        amount: "",
        rec_amt: 0.0,
      };
      return updated;
    });

    // ✅ Get current party ID
    const currentPartyId = rows[index]?.party || selectedPartyId;
    const currentProjectId = rows[index]?.project || null;
    if (type === "advance") {
      if (!currentPartyId) {
        toast.warning("Please select Debtor first!");
        setPurchaseLists([]);
        return;
      }

      fetchAdvanceData(currentPartyId, currentProjectId);
    }

    if (type === "sale") {
      if (!currentPartyId) {
        toast.warning("Please select Debtor first!");
        setSaleList([]);
        return;
      }

      fetchSaleData(currentPartyId); // ✅ PASS PARTY ID
    }
  };

  const handleRowChange = (index, e) => {
    const { name, value, files } = e.target;

    // auto‑load bank list if needed
    if (
      name === "payment_mode" &&
      value === "Bank" &&
      bankDetails.length === 0
    ) {
      fetchBankData();
    }

    setRows((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };

      // ✅ File upload
      if (name === "attachment" && files) {
        row.attachment = files[0];
      } else if (name === "rec_amt") {
        const entered = Number(value || 0);
        const limit = Number(row.amount || 0);

        // ⚙️ Ab koi restriction nahi — rec_amt amount se zyada ho sakta hai
        if (entered < 0) {
          row.rec_amt = 0;
        } else {
          row.rec_amt = entered;
        }
      } else {
        row[name] = value;
      }

      updated[index] = row;
      return updated;
    });
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    rows.forEach((row, index) => {
      // Payment mode validation
      if (!row.payment_mode) {
        errors[`payment_mode_${index}`] = "Payment mode is required";
        isValid = false;
      }

      // Party validation
      if (!row.party) {
        errors[`party_${index}`] = "Party is required";
        isValid = false;
      }

      // Amount validation (only if voucher type selected)

      // Advance PI validation
      if (row.voucher_type === "advance" && !row.proforma_invoice) {
        errors[`proforma_invoice_${index}`] = "Please select an advance/PI";
        isValid = false;
      }

      // Sales voucher validation
      if (row.voucher_type === "sale" && !row.sales_voucher) {
        errors[`sales_voucher_${index}`] = "Please select a sales voucher";
        isValid = false;
      }

      // Bank validation
      if (row.payment_mode === "Bank" && !row.bank) {
        errors[`bank_${index}`] = "Please select bank details";
        isValid = false;
      }

      // Received amount validation (mandatory regardless of voucher type)
      if (!row.rec_amt || parseFloat(row.rec_amt) <= 0) {
        errors[`rec_amt_${index}`] = "Received amount is required";
        isValid = false;
      }
    });

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!storedDivision?.division_id) {
    toast.error("No division selected. Please select a division from the header.");
    return;
  }

    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    const userId = Number(localStorage.getItem("user_id"));

    // ✅ Fields jo clear ho sakte hain (null bhej sakte hain)
    const clearableFields = [
      "project",
      "proforma_invoice",
      "sales_voucher",
      "bank",
    ];

    try {
      if (editMode) {
        const row = rows[0];
        const formData = new FormData();

        Object.keys(row).forEach((key) => {
        if (key === "attachment") {
          if (row[key] instanceof File) {
            formData.append(key, row[key]);
          }
        }
        else if (clearableFields.includes(key)) {
          if (row[key] !== null && row[key] !== "" && row[key] !== undefined) {
            formData.append(key, row[key]);
          } else {
            formData.append(key, "");
          }
        }
        else if (key === "division") {
          formData.append(key, Number(storedDivision.division_id));
        }
        else if (["party"].includes(key)) {
          if (row[key] !== null && row[key] !== "") {
            formData.append(key, Number(row[key]));
          }
        }
        else if (row[key] !== null && row[key] !== "") {
          formData.append(key, row[key]);
        }
      });

        await ReceiptVoucherUpdate(currentEditId, formData);
        toast.success("Receipt voucher updated successfully!");
        navigate("/dashboard/receiptvoucher");
      } else {
        // Create mode - same as before
        for (const row of rows) {
          const formData = new FormData();
          Object.keys(row).forEach((key) => {
            if (key === "attachment" && row[key] instanceof File) {
              formData.append(key, row[key]);
            } else if (row[key] !== null && row[key] !== "") {
              if (["party", "bank", "division"].includes(key)) {
                formData.append(key, Number(row[key]));
              } else {
                formData.append(key, row[key]);
              }
            }
          });
          formData.append("created_by", userId);
          await ReceiptVoucherCreate(formData);
        }

        toast.success("Receipt voucher created successfully!");
        setRows([blankRow()]);
        generateVoucherNumber();
        navigate("/dashboard/receiptvoucher");
      }

      setFormErrors({});
    } catch (error) {
      toast.error(editMode ? "Update failed" : "Creation failed");
    }
  };
  return (
    <>
      <Grid>
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            display: "flex",
            justifyContent: isMobile ? "flex-start" : "flex-end",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 2 : 3,
            alignItems: isMobile ? "stretch" : "flex-end",
            width: "100%",
            mt: isMobile ? -2 : -3,
            pt: 0,
            mb: 2,
            px: 1,
            ml: -1,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            fullWidth={isMobile}
            sx={{
              borderRadius: 0,
              height: isMobile ? 45 : 35, // 🔹 Mobile pe bada
              minWidth: isMobile ? "100%" : 120,
              fontSize: isMobile ? "0.9rem" : "0.7rem", // 🔹 Mobile pe font bada
              px: 2,
              textTransform: "none",
              fontWeight: isMobile ? 500 : 400, // 🔹 Mobile pe bold
            }}
          >
            ← Back
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            fullWidth={isMobile}
            sx={{
              borderRadius: 0,
              height: isMobile ? 45 : 35, // 🔹 Mobile pe bada
              minWidth: isMobile ? "100%" : 140,
              fontSize: isMobile ? "0.9rem" : "0.7rem", // 🔹 Mobile pe font bada
              px: 2,
              textTransform: "none",
              fontWeight: isMobile ? 600 : 400, // 🔹 Mobile pe bold
              backgroundColor: isMobile ? "#1976d2" : undefined, // 🔹 Mobile pe bright blue
              "&:hover": {
                backgroundColor: isMobile ? "#1565c0" : undefined,
              },
            }}
          >
            {editMode ? "Update" : "Submit"}
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ p: isMobile ? 1 : 3, maxWidth: 1400, mx: "auto" }}>
        <ToastContainer position="top-right" autoClose={3000} />

        <form onSubmit={handleSubmit}>
          {rows.map((row, index) => (
            <Card key={index} sx={{ mb: 3, backgroundColor: "#fff" }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                {/* Top 3 Fields in One Row - Mobile responsive */}
                <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Voucher No."
                      name="receipt_number"
                      value={row.receipt_number || ""}
                      size="small"
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Invoice No."
                      name="invoice_number"
                      value={row.invoice_number || ""}
                      onChange={(e) => handleRowChange(index, e)}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Invoice Date"
                      name="invoice_date"
                      type="date"
                      value={row.invoice_date || ""}
                      onChange={(e) => handleRowChange(index, e)}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4} sx={{ p: 1 }}>
                    <Autocomplete
                      options={projectList || []}
                      getOptionLabel={(option) => {
                        const name =
                          option?.company_name ||
                          option?.project_name ||
                          option?.name ||
                          "";

                        return `${name}`;
                      }}
                      value={
                        projectList.find((p) => p.id === row.project) || null
                      }
                      onChange={(e, newValue) =>
                        handleProjectChange(index, newValue)
                      }
                      onInputChange={(e, inputValue, reason) => {
                        if (reason === "input") {
                          fetchProjectList(inputValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Project"
                          size="small"
                          sx={{ "& .MuiInputBase-root": { height: 42 } }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ p: 1 }}>
                    <Autocomplete
                      options={partyList || []}
                      getOptionLabel={(option) =>
                        option?.company_name || option?.company_name || ""
                      }
                      value={
                        partyList.find((p) => {
                          const partyId = p.party_id || p.id;
                          return String(partyId) === String(row.party || "");
                        }) || null
                      }
                      onChange={(e, newValue) =>
                        handlePartyChange(index, newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Debtor"
                          size="small"
                          sx={{ "& .MuiInputBase-root": { height: 42 } }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => {
                        const optionId = option?.party_id || option?.id;
                        const valueId = value?.party_id || value?.id;
                        return String(optionId) === String(valueId);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4} sx={{ p: 1 }}>
                    <TextField
                      fullWidth
                      label="Division"
                      value={storedDivision?.division || "No Division Selected"}
                      disabled
                      size="small"
                      InputProps={{
                        sx: {
                          height: 42,
                          backgroundColor: "#f5f5f5",
                        },
                      }}
                      helperText="Division is set globally"
                      error={!storedDivision?.division_id}
                    />
                  </Grid>
                </Grid>

                {/* Voucher Type Section - Mobile responsive */}
                <Grid container spacing={isMobile ? 2 : 3}>
                  {/* Left Side - Voucher Type Selection */}
                  <Grid item xs={12} md={3}>
                    <FormControl sx={{ alignItems: "flex-start" }} fullWidth>
                      <FormLabel
                        component="legend"
                        sx={{
                          alignItems: "flex-start",
                          fontWeight: 600,
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        Select Voucher Type *
                      </FormLabel>
                      <RadioGroup
                        value={row.voucher_type || ""}
                        onChange={(e) =>
                          handleVoucherTypeChange(index, e.target.value)
                        }
                      >
                        <FormControlLabel
                          value="advance"
                          control={<Radio />}
                          label="Advance/PI"
                          sx={{ mb: 0 }}
                          disabled={
                            editMode &&
                            (row.voucher_type === "sale" ||
                              row.voucher_type === "on_account")
                          }
                        />
                        <FormControlLabel
                          value="sale"
                          control={<Radio />}
                          label="Sale Voucher"
                          sx={{ mb: 0 }}
                          disabled={
                            editMode &&
                            (row.voucher_type === "advance" ||
                              row.voucher_type === "on_account")
                          }
                        />
                        <FormControlLabel
                          value="on_account"
                          control={<Radio />}
                          label="On Account"
                          sx={{ mb: 0 }}
                          disabled={
                            editMode &&
                            (row.voucher_type === "advance" ||
                              row.voucher_type === "sale")
                          }
                        />
                      </RadioGroup>
                      {formErrors[`voucher_type_${index}`] && (
                        <Typography variant="caption" color="error">
                          {formErrors[`voucher_type_${index}`]}
                        </Typography>
                      )}
                      {editMode && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Voucher type cannot be changed in edit mode
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Right Side - Dynamic Content Based on Selection */}
                  <Grid item xs={12} md={9}>
                    <Grid container spacing={isMobile ? 1.5 : 2}>
                      {row.voucher_type === "advance" && (
                        <>
                          <Grid item xs={12} md={6}>
                            <Autocomplete
                              options={purchaseLists}
                              getOptionLabel={(option) => option.pi_no || ""}
                              value={
                                purchaseLists.find(
                                  (p) =>
                                    p.invoice_number === row.proforma_invoice,
                                ) || null
                              }
                              onChange={(e, newValue) =>
                                handleAdvanceChange(index, newValue)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Select Advance/PI *"
                                  size="small"
                                  error={
                                    !!formErrors[`proforma_invoice_${index}`]
                                  }
                                  helperText={
                                    formErrors[`proforma_invoice_${index}`]
                                  }
                                />
                              )}
                              isOptionEqualToValue={(option, value) =>
                                option?.invoice_number === value?.invoice_number
                              }
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Amount"
                              value={row.amount || ""}
                              disabled
                              size="small"
                            />
                          </Grid>
                        </>
                      )}

                      {/* Sales Voucher Fields */}
                      {row.voucher_type === "sale" && (
                        <>
                          <Grid item xs={12} md={6}>
                            <Autocomplete
                              options={saleList}
                              getOptionLabel={(option) =>
                                option.sales_number || option.name || ""
                              }
                              value={
                                saleList.find(
                                  (s) =>
                                    s.sales_voucher_no === row.sales_voucher,
                                ) || null
                              }
                              onChange={(e, newValue) =>
                                handleSaleChange(index, newValue)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Select Sale Voucher *"
                                  size="small"
                                  error={!!formErrors[`sales_voucher_${index}`]}
                                  helperText={
                                    formErrors[`sales_voucher_${index}`]
                                  }
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Amount"
                              value={row.amount || ""}
                              disabled
                              size="small"
                            />
                          </Grid>
                        </>
                      )}

                      {/* Received Amount */}
                      <Grid item xs={12} md={12}>
                        <TextField
                          fullWidth
                          label="Received Amount *"
                          name="rec_amt"
                          value={row.rec_amt || ""}
                          onChange={(e) => handleRowChange(index, e)}
                          size="small"
                          type="number"
                          inputProps={{ min: 0, max: row.amount || 0 }}
                          required
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Divider */}
                <Divider sx={{ my: 3, width: "100%" }} />

                {/* Payment Mode Section - Mobile responsive */}
                <Grid container spacing={isMobile ? 2 : 3}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth sx={{ alignItems: "flex-start" }}>
                      <FormLabel
                        component="legend"
                        sx={{
                          alignItems: "flex-start",
                          fontWeight: 600,
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        Payment Mode *
                      </FormLabel>
                      <RadioGroup
                        value={row.payment_mode || ""}
                        onChange={(e) => handleRowChange(index, e)}
                        name="payment_mode"
                      >
                        <FormControlLabel
                          value="Cash"
                          control={<Radio />}
                          label="Cash"
                          sx={{ mb: 0 }}
                        />
                        <FormControlLabel
                          value="Bank"
                          control={<Radio />}
                          label="Bank"
                          sx={{ mb: 0 }}
                        />
                      </RadioGroup>
                      {formErrors[`payment_mode_${index}`] && (
                        <Typography variant="caption" color="error">
                          {formErrors[`payment_mode_${index}`]}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={9}>
                    <Grid container spacing={isMobile ? 1.5 : 2}>
                      {row.payment_mode === "Cash" && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Cash Details"
                            name="description"
                            value={row.description || ""}
                            onChange={(e) => handleRowChange(index, e)}
                            size="small"
                            rows={2}
                          />
                        </Grid>
                      )}

                      {row.payment_mode === "Bank" && (
                        <>
                          <Grid item xs={12} md={4}>
                            <Autocomplete
                              options={bankDetails}
                              getOptionLabel={(option) =>
                                `${option.account_number} - ${
                                  option.bank_name || "Bank"
                                }`
                              }
                              value={
                                bankDetails.find(
                                  (bank) => bank.id === row.bank,
                                ) || null
                              }
                              onChange={(e, newValue) => {
                                handleRowChange(index, {
                                  target: {
                                    name: "bank",
                                    value: newValue ? newValue.id : "",
                                  },
                                });
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Bank Details *"
                                  size="small"
                                  error={!!formErrors[`bank_${index}`]}
                                  helperText={formErrors[`bank_${index}`]}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Check Date"
                              name="cheque_date"
                              InputLabelProps={{ shrink: true }}
                              type="date"
                              value={row.cheque_date || ""}
                              onChange={(e) => handleRowChange(index, e)}
                              size="small"
                              sx={{ "& .MuiInputBase-root": { height: 42 } }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Cheque/NEFT/RTGS/UPI"
                              name="cheque_no"
                              value={row.cheque_no || ""}
                              onChange={(e) => handleRowChange(index, e)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box>
                              <TextField
                                fullWidth
                                type="file"
                                name="attachment"
                                onChange={(e) => {
                                  handleRowChange(index, e);
                                  setExistingFileName("");
                                }}
                                label={
                                  existingFileName
                                    ? "Change Attachment"
                                    : "Attachment"
                                }
                                size="small"
                                InputLabelProps={{ shrink: true }}
                              />

                              {existingFileName &&
                                row.attachment &&
                                editMode && (
                                  <Box
                                    mt={0.5}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    sx={{
                                      fontSize: isMobile ? "0.7rem" : "0.75rem",
                                      color: "text.secondary",
                                    }}
                                  >
                                    <span>Current: {existingFileName}</span>
                                    {typeof row.attachment === "string" && (
                                      <IconButton
                                        size="small"
                                        component="a"
                                        href={row.attachment}
                                        target="_blank"
                                      >
                                        <Visibility
                                          sx={{
                                            fontSize: "1rem",
                                            color: "#1976d2",
                                          }}
                                        />
                                      </IconButton>
                                    )}
                                  </Box>
                                )}
                            </Box>
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Receipt Date"
                          name="receipt_date"
                          InputLabelProps={{ shrink: true }}
                          type="date"
                          value={row.receipt_date || ""}
                          onChange={(e) => handleRowChange(index, e)}
                          size="small"
                          sx={{ "& .MuiInputBase-root": { height: 42 } }}
                          inputProps={{
                            max: new Date().toISOString().split("T")[0],
                          }}
                        />
                      </Grid>

                      {row.payment_mode !== "Bank" && (
                        <Grid item xs={12} md={6}>
                          <Box>
                            <TextField
                              fullWidth
                              type="file"
                              name="attachment"
                              onChange={(e) => {
                                handleRowChange(index, e);
                                setExistingFileName("");
                              }}
                              label={
                                existingFileName
                                  ? "Change Attachment"
                                  : "Attachment"
                              }
                              size="small"
                              InputLabelProps={{ shrink: true }}
                            />

                            {existingFileName && row.attachment && editMode && (
                              <Box
                                mt={0.5}
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{
                                  fontSize: isMobile ? "0.7rem" : "0.75rem",
                                  color: "text.secondary",
                                }}
                              >
                                <span>Current: {existingFileName}</span>
                                {typeof row.attachment === "string" && (
                                  <a
                                    href={row.attachment}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#1976d2",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    View
                                  </a>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      )}

                      <Grid item xs={12} md={12}>
                        <TextField
                          fullWidth
                          label="Narration"
                          name="narration"
                          value={row.narration || ""}
                          onChange={(e) => handleRowChange(index, e)}
                          size="small"
                          rows={isMobile ? 3 : 0}
                          multiline
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </form>
      </Box>
    </>
  );
};

export default ReceiptVoucherForm;
